import { z } from 'zod';
import { companyIdSchema } from './company';

const isoDateTimeSchema = z.iso.datetime({
  error: 'La date doit être au format ISO 8601.',
});

const nonNegativeIntSchema = z.number().int().nonnegative();

const positiveCompanyTokenIntSchema = z.int({
  error: 'Le nombre de tokens doit être un entier positif.',
}).positive({
  error: 'Le nombre de tokens doit être strictement positif.',
});

export const accountTypeSchema = z.enum(['demo', 'subscriber'], {
  error: 'Le type de compte est invalide.',
});

export const companySubscriptionTypeSchema = z.enum(['standard'], {
  error: 'Le type d’abonnement de l’entreprise est invalide.',
});

export const quotaEventTypeSchema = z.enum([
  'account_type_changed',
  'auto_renewed',
  'blocked',
  'enforcement_toggled',
  'extra_credit_added',
  'limit_updated',
  'manual_reset',
  'quota_configured',
  'recomputed',
  'renewal_date_updated',
], {
  error: 'Le type d’événement quota est invalide.',
});

export const companyQuotaEventTypeSchema = z.enum([
  'auto_renewed',
  'blocked',
  'extra_credit_added',
  'limit_updated',
  'manual_reset',
  'quota_configured',
  'recomputed',
  'subscription_type_updated',
], {
  error: 'Le type d’événement quota entreprise est invalide.',
});

export const quotaPeriodClosedReasonSchema = z.enum(['manual_reset', 'renewal'], {
  error: 'La raison de cloture de periode est invalide.',
});

export const quotaStatusSchema = z.object({
  accountType: accountTypeSchema,
  blocked: z.boolean(),
  enforced: z.boolean(),
  extraTokens: nonNegativeIntSchema.nullable(),
  periodStartAt: isoDateTimeSchema.nullable(),
  renewalIntervalDays: z.number().int().nullable(),
  renewsAt: isoDateTimeSchema.nullable(),
  tokenLimit: nonNegativeIntSchema.nullable(),
  tokensConsumed: nonNegativeIntSchema.nullable(),
  tokensRemaining: nonNegativeIntSchema.nullable(),
  usageRatio: z.number().min(0).nullable(),
});

export const adminQuotaConfigSchema = z.object({
  enforcementEnabled: z.boolean().default(true),
  renewalIntervalDays: z.number().int().min(1, {
    error: 'L intervalle de renouvellement doit etre d au moins 1 jour.',
  }).max(365, {
    error: 'L intervalle de renouvellement ne peut pas depasser 365 jours.',
  }).default(30),
  renewsAt: isoDateTimeSchema,
  tokenLimit: z.number().int().min(1, {
    error: 'La limite de tokens doit etre d au moins 1.',
  }).max(100_000_000_000, {
    error: 'La limite de tokens est trop elevee.',
  }),
});

export const adminQuotaAddExtraSchema = z.object({
  reason: z.string().trim().max(500, {
    error: 'La raison ne peut pas dépasser 500 caractères.',
  }).optional(),
  tokens: z.number().int().min(1, {
    error: 'Le nombre de tokens doit être d’au moins 1.',
  }).max(100_000_000_000, {
    error: 'Le nombre de tokens est trop élevé.',
  }),
});

export const adminQuotaResetSchema = z.object({
  reason: z.string().trim().max(500, {
    error: 'La raison ne peut pas depasser 500 caracteres.',
  }).optional(),
});

export const companyQuotaConfigSchema = z.object({
  monthlyTokenLimit: positiveCompanyTokenIntSchema.nullable(),
  subscriptionType: companySubscriptionTypeSchema,
});

export const companyQuotaStatusSchema = z.object({
  blocked: z.boolean(),
  extraTokens: nonNegativeIntSchema,
  monthlyTokenLimit: positiveCompanyTokenIntSchema.nullable(),
  resetAt: isoDateTimeSchema.nullable(),
  tokensConsumed: nonNegativeIntSchema,
});

export const adminCompanyQuotaDetailSchema = z.object({
  config: companyQuotaConfigSchema.nullable(),
  status: companyQuotaStatusSchema,
});

export const quotaMeStatusSchema = quotaStatusSchema.extend({
  company: companyQuotaStatusSchema,
});

export const adminCompanyQuotaUpdateSchema = companyQuotaConfigSchema;

export const adminCompanyQuotaExtraSchema = adminQuotaAddExtraSchema;

export const adminAccountTypeChangeSchema = z.object({
  accountType: accountTypeSchema,
  // Bascule vers démo : durée d'accès (null = sans expiration).
  demoExpiresAt: isoDateTimeSchema.nullable().optional(),
  quota: adminQuotaConfigSchema.optional(),
  reason: z.string().trim().max(500, {
    error: 'La raison ne peut pas depasser 500 caracteres.',
  }).optional(),
});

export const quotaEventSchema = z.object({
  actorId: z.string().trim().min(1).nullable(),
  actorName: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  eventType: quotaEventTypeSchema,
  id: z.uuid({
    error: 'L’identifiant d’événement quota est invalide.',
  }),
  reason: z.string().nullable(),
  snapshot: z.record(z.string(), z.unknown()).nullable(),
  tokensDelta: z.number().int().nullable(),
});

export const companyQuotaEventSchema = quotaEventSchema.extend({
  companyId: companyIdSchema,
  eventType: companyQuotaEventTypeSchema,
});

export const adminCompanyQuotaParamsSchema = z.object({
  companyId: companyIdSchema,
});

export const companyQuotaEventsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(5).max(200).default(20),
});

export const adminCompanyWithQuotaSchema = z.object({
  createdAt: isoDateTimeSchema,
  id: companyIdSchema,
  name: z.string().trim().min(1),
  quota: adminCompanyQuotaDetailSchema,
  slug: z.string().trim().min(1),
  /** Cumul des tokens consommés par l'entreprise, tous usages confondus. */
  totalTokensConsumed: nonNegativeIntSchema,
  userCount: nonNegativeIntSchema,
});

export const adminCompaniesQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  // Défaut généreux : les consommateurs hors tableau (combobox, résolution de
  // nom) attendent la liste complète ; le tableau passe sa propre pageSize.
  pageSize: z.coerce.number().int().min(5).max(200).default(200),
  search: z.string().trim().max(120).optional().transform((value) => value || undefined),
  sortBy: z.enum(['consumed', 'createdAt', 'members', 'name', 'status']).default('name'),
  sortDirection: z.enum(['asc', 'desc']).default('asc'),
  status: z.enum(['all', 'active', 'blocked']).default('all'),
});

export const adminCompaniesWithQuotaSchema = z.object({
  data: z.array(adminCompanyWithQuotaSchema),
  meta: z.object({
    filteredTotal: nonNegativeIntSchema,
    page: nonNegativeIntSchema.min(1),
    pageCount: nonNegativeIntSchema.min(1),
    pageSize: nonNegativeIntSchema.min(1),
    total: nonNegativeIntSchema,
  }),
  success: z.literal(true),
});

export const quotaPeriodSchema = z.object({
  closedReason: quotaPeriodClosedReasonSchema,
  extraTokens: nonNegativeIntSchema,
  id: z.uuid({
    error: 'L identifiant de periode quota est invalide.',
  }),
  periodEndAt: isoDateTimeSchema,
  periodStartAt: isoDateTimeSchema,
  tokenLimit: nonNegativeIntSchema,
  tokensConsumed: nonNegativeIntSchema,
});

const paginationMetaSchema = z.object({
  page: nonNegativeIntSchema.min(1),
  pageCount: nonNegativeIntSchema.min(1),
  pageSize: nonNegativeIntSchema.min(1),
  total: nonNegativeIntSchema,
});

export const paginatedQuotaEventsSchema = z.object({
  items: z.array(quotaEventSchema),
  meta: paginationMetaSchema,
});

export const paginatedCompanyQuotaEventsSchema = z.object({
  items: z.array(companyQuotaEventSchema),
  meta: paginationMetaSchema,
});

export const paginatedQuotaPeriodsSchema = z.object({
  items: z.array(quotaPeriodSchema),
  meta: paginationMetaSchema,
});

export const adminUserQuotaDetailSchema = z.object({
  config: adminQuotaConfigSchema.nullable(),
  status: quotaStatusSchema,
  user: z.object({
    accountType: accountTypeSchema,
    companyName: z.string().nullable(),
    demoExpiresAt: isoDateTimeSchema.nullable(),
    email: z.email({
      error: 'Adresse email invalide.',
    }),
    id: z.string().trim().min(1, {
      error: 'L identifiant utilisateur est requis.',
    }),
    name: z.string().trim().min(1, {
      error: 'Le nom est requis.',
    }),
  }),
});

export type AccountType = z.infer<typeof accountTypeSchema>;
export type CompanySubscriptionType = z.infer<typeof companySubscriptionTypeSchema>;
export type QuotaEventType = z.infer<typeof quotaEventTypeSchema>;
export type CompanyQuotaEventType = z.infer<typeof companyQuotaEventTypeSchema>;
export type QuotaPeriodClosedReason = z.infer<typeof quotaPeriodClosedReasonSchema>;
export type QuotaStatusDTO = z.infer<typeof quotaStatusSchema>;
export type AdminQuotaConfigDTO = z.infer<typeof adminQuotaConfigSchema>;
export type AdminQuotaAddExtraDTO = z.infer<typeof adminQuotaAddExtraSchema>;
export type AdminQuotaResetDTO = z.infer<typeof adminQuotaResetSchema>;
export type CompanyQuotaConfigDTO = z.infer<typeof companyQuotaConfigSchema>;
export type CompanyQuotaStatusDTO = z.infer<typeof companyQuotaStatusSchema>;
export type AdminCompanyQuotaDetailDTO = z.infer<typeof adminCompanyQuotaDetailSchema>;
export type QuotaMeStatusDTO = z.infer<typeof quotaMeStatusSchema>;
export type AdminCompanyQuotaUpdateDTO = z.infer<typeof adminCompanyQuotaUpdateSchema>;
export type AdminCompanyQuotaExtraDTO = z.infer<typeof adminCompanyQuotaExtraSchema>;
export type AdminAccountTypeChangeDTO = z.infer<typeof adminAccountTypeChangeSchema>;
export type QuotaEventDTO = z.infer<typeof quotaEventSchema>;
export type CompanyQuotaEventDTO = z.infer<typeof companyQuotaEventSchema>;
export type CompanyQuotaEventsQuery = z.infer<typeof companyQuotaEventsQuerySchema>;
export type AdminCompanyWithQuotaDTO = z.infer<typeof adminCompanyWithQuotaSchema>;
export type AdminCompaniesQueryDTO = z.input<typeof adminCompaniesQuerySchema>;
export type AdminCompaniesWithQuotaDTO = z.infer<typeof adminCompaniesWithQuotaSchema>;
export type QuotaPeriodDTO = z.infer<typeof quotaPeriodSchema>;
export type PaginatedQuotaEventsDTO = z.infer<typeof paginatedQuotaEventsSchema>;
export type PaginatedCompanyQuotaEventsDTO = z.infer<typeof paginatedCompanyQuotaEventsSchema>;
export type PaginatedQuotaPeriodsDTO = z.infer<typeof paginatedQuotaPeriodsSchema>;
export type AdminUserQuotaDetailDTO = z.infer<typeof adminUserQuotaDetailSchema>;
